import { isMetricDeltaVisible, isMetricVisible } from '@/features/shared/metricVisibility';
import { resolveEntityTags } from '@/features/shared/tags';
import {
  getDateFilterOperators,
  getNumericFilterOperators,
  getSingleSelectFilterOperators,
} from '@/ui/composites/grid/gridOperators';
import { TagsInlineCell } from '@/ui/composites/table';
import { buildMetricColumns, type MetricColumnKey } from '@/ui/composites/table/tableUtils';
import { type GridColDef } from '@/ui/primitives';
import { CloneActionsCell } from './testsColumns.cloneCells';
import { buildCoreColumns } from './testsColumns.coreColumns';
import { buildHiddenFilterColumns } from './testsColumns.hiddenFilterColumns';
import { buildSparklineColumns } from './testsColumns.sparklineColumns';
import type { TestsColumnsOptions } from './testsColumns.types';
import type { MetricThresholdGroup, TestListItemDto } from '@flowqa/shared';

const columnFlex = {
  name: 1,
  suite: 1,
  latest: 0.25,
  lastExecuted: 0.25,
  passRate: 0.75,
  frequency: 0.75,
  avgDuration: 0.75,
  flakeScore: 0.5,
  mttf: 0.5,
  delta: 0.4,
  tags: 0.75,
  actions: 0.1,
};

export const buildTestsColumns = ({
  selectedProjectId,
  onProjectSelect,
  onStatusSelect,
  thresholds,
  resolveThresholds,
  showSuiteLinks,
  combineNameAndSuite = false,
  includeTagsInNameColumn = false,
  includeTagsColumn = true,
  includeActionsColumn = false,
  disableColumnResizeHandles = false,
  sparklineRangeDays: _sparklineRangeDays = 7,
  passRateAsSparklineCell = false,
  frequencyAsSparklineCell = false,
  avgDurationAsSparklineCell = false,
  latestAsMeta = false,
  includeLastExecutedColumn = true,
  showDeltas = true,
  disableLinks = false,
  metricSeriesByTestId,
  filterSuites = [],
  filterTags = [],
  onDeleteRow,
  deleteRowPendingId = null,
  tagColorByName = {},
  onUpdateRowTagsColor,
  updateRowTagsColorPendingId = null,
}: TestsColumnsOptions): Array<GridColDef<TestListItemDto>> => {
  const singleSelectOperators = getSingleSelectFilterOperators();
  const dateOperators = getDateFilterOperators();
  const numericOperators = getNumericFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: TestListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };
  const metricKeys: MetricColumnKey[] = [
    'passRate',
    'frequency',
    'avgDuration',
    ...(isMetricVisible('flakeScore') ? (['flakeScore'] as const) : []),
    ...(isMetricVisible('mttf') ? (['mttf'] as const) : []),
  ];
  const enabledMetricKeys = metricKeys.filter((key) => {
    if (passRateAsSparklineCell && key === 'passRate') return false;
    if (frequencyAsSparklineCell && key === 'frequency') return false;
    if (avgDurationAsSparklineCell && key === 'avgDuration') return false;
    return true;
  });

  const { nameColumn, suiteColumn, latestSummaryColumn, lastExecutedColumn } = buildCoreColumns({
    onProjectSelect,
    onStatusSelect,
    showSuiteLinks,
    combineNameAndSuite,
    includeTagsInNameColumn,
    latestAsMeta,
    includeLastExecutedColumn,
    disableLinks,
    tagColorByName,
    columnFlex: {
      name: columnFlex.name,
      suite: columnFlex.suite,
      latest: columnFlex.latest,
      lastExecuted: columnFlex.lastExecuted,
    },
  });

  const { passRateSparklineColumn, frequencySparklineColumn, avgDurationSparklineColumn } =
    buildSparklineColumns({
      passRateAsSparklineCell,
      frequencyAsSparklineCell,
      avgDurationAsSparklineCell,
      showDeltas,
      resolvedThresholds,
      metricSeriesByTestId,
      columnFlex: {
        passRate: columnFlex.passRate,
        frequency: columnFlex.frequency,
        avgDuration: columnFlex.avgDuration,
      },
    });

  const metricColumns = buildMetricColumns<TestListItemDto>({
    enabledKeys: enabledMetricKeys,
    includeDeltas: (key) => {
      if (!deltasEnabled) return false;
      if (passRateAsSparklineCell && key === 'passRate') return false;
      if (frequencyAsSparklineCell && key === 'frequency') return false;
      if (avgDurationAsSparklineCell && key === 'avgDuration') return false;
      if (key === 'flakeScore') return isMetricDeltaVisible('flakeScore');
      if (key === 'mttf') return isMetricDeltaVisible('mttf');
      return true;
    },
    fields: {
      passRate: 'metrics.passRate',
      frequency: 'metrics.frequencyPerDay',
      avgDuration: 'metrics.averageDurationMs',
      flakeScore: 'metrics.flakeScore',
      mttf: 'metrics.mttfSeconds',
    },
    headers: {
      avgDuration: 'Average Duration',
    },
    deltaFields: {
      passRate: 'metrics.delta.passRate',
      frequency: 'metrics.delta.frequencyPerDay',
      avgDuration: 'metrics.delta.averageDurationMs',
      flakeScore: 'metrics.delta.flakeScore',
      mttf: 'metrics.delta.mttfSeconds',
    },
    flex: {
      passRate: columnFlex.passRate,
      frequency: columnFlex.frequency,
      avgDuration: columnFlex.avgDuration,
      flakeScore: columnFlex.flakeScore,
      mttf: columnFlex.mttf,
    },
    deltaFlex: {
      passRate: columnFlex.delta,
      frequency: columnFlex.delta,
      avgDuration: columnFlex.delta,
      flakeScore: columnFlex.delta,
      mttf: columnFlex.delta,
    },
    thresholds: thresholds,
    resolveThresholds: (row) => resolvedThresholds(row),
    align: 'right',
    headerAlign: 'right',
  });

  const {
    suiteFilterColumn,
    tagsFilterColumn,
    durationFilterColumn,
    timestampFilterColumn,
    statusFilterColumn,
  } = buildHiddenFilterColumns({
    singleSelectOperators,
    numericOperators,
    dateOperators,
    filterSuites,
    filterTags,
  });

  const columns: Array<GridColDef<TestListItemDto>> = [
    nameColumn,
    ...(combineNameAndSuite ? [suiteFilterColumn] : showSuiteLinks ? [suiteColumn] : []),
    ...(combineNameAndSuite && includeTagsInNameColumn && !includeTagsColumn
      ? [tagsFilterColumn]
      : []),
    latestSummaryColumn,
    ...(lastExecutedColumn ? [lastExecutedColumn] : []),
    ...(latestAsMeta ? [statusFilterColumn, durationFilterColumn, timestampFilterColumn] : []),
    ...(passRateSparklineColumn ? [passRateSparklineColumn] : []),
    ...(frequencySparklineColumn ? [frequencySparklineColumn] : []),
    ...(avgDurationSparklineColumn ? [avgDurationSparklineColumn] : []),
    ...metricColumns,
    ...(includeTagsColumn
      ? [
          {
            field: 'tags',
            headerName: 'Tags',
            flex: columnFlex.tags,
            filterable: false,
            sortable: false,
            renderCell: (params) => <TagsInlineCell tags={resolveEntityTags(params.row)} />,
          } satisfies GridColDef<TestListItemDto>,
        ]
      : []),
    ...(includeActionsColumn
      ? [
          {
            field: 'actions',
            headerName: '',
            flex: columnFlex.actions,
            minWidth: 56,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => (
              <CloneActionsCell
                rowName={params.row.name}
                rowTags={resolveEntityTags(params.row)}
                rowTagColors={resolveEntityTags(params.row).map(
                  (tag) => tagColorByName[tag] ?? 'neutral',
                )}
                onUpdateTagsColor={(input) => {
                  onUpdateRowTagsColor?.({ row: params.row, ...input });
                }}
                onDelete={() => {
                  onDeleteRow?.(params.row);
                }}
                disableDelete={deleteRowPendingId === params.row.id}
                updateTagsColorLoading={updateRowTagsColorPendingId === params.row.id}
              />
            ),
          } satisfies GridColDef<TestListItemDto>,
        ]
      : []),
  ];

  if (!disableColumnResizeHandles) {
    return columns;
  }

  return columns.map((column) => ({
    ...column,
    resizable: false,
  }));
};
